import { setMilliseconds, sub } from 'date-fns';
import { createWithEqualityFn as create } from 'zustand/traditional';

import { VIEW_BY, ViewByValue } from 'components/insights/referrals/constants';
import {
  filtersForSelectedGroupType,
  receivingClientFilterKey,
  receivingGroupFilterKey,
} from 'components/insights/referrals/helpers/filters';
import { DATES, END_DATE, START_DATE } from 'constants/filterKeysConstants';
import GroupType from 'models/GroupType';

import { FilterOption, FilterState } from './types';

const now = setMilliseconds(new Date(), 0);
const thirtyDaysAgo = sub(now, { days: 30 });

export type ReferralsInsightsFilterState = FilterState<FilterOption[] | string | Date>;

type Actions = {
  setSelectedReceivingGroupType: (receivingGroupType: GroupType | undefined) => void;
  setSendingGroupTypeId: (id: string | undefined) => void;
  setViewBy: (viewBy: ViewByValue) => void;
  setSelectedFilters: (filters: ReferralsInsightsFilterState) => void;
  resetFilters: () => void;
  setExpanded: (expanded: boolean) => void;
};

type State = {
  selectedReceivingGroupType: GroupType | undefined;
  sendingGroupTypeId: string | undefined;
  viewBy: ViewByValue;
  filters: ReferralsInsightsFilterState;
  expanded: boolean;
  actions: Actions;
};

export const useReferralsInsightsStore = create<State>((set) => ({
  selectedReceivingGroupType: undefined,
  sendingGroupTypeId: undefined,
  viewBy: VIEW_BY.SENDING_GROUP,
  expanded: false,
  filters: {
    [START_DATE]: thirtyDaysAgo,
    [END_DATE]: now,
  },
  actions: {
    setSelectedReceivingGroupType: (receivingGroupType) => set({ selectedReceivingGroupType: receivingGroupType }),
    setSendingGroupTypeId: (id) => set({ sendingGroupTypeId: id }),
    setViewBy: (viewBy) => set({ viewBy }),
    setSelectedFilters: (filters) => set((state) => ({ filters: { ...state.filters, ...filters } })),
    resetFilters: () =>
      set({
        viewBy: VIEW_BY.SENDING_GROUP,
        filters: {
          [START_DATE]: thirtyDaysAgo,
          [END_DATE]: now,
        },
      }),
    setExpanded: (expanded) => set({ expanded }),
  },
}));

export const useReferralsInsightsActions = () => useReferralsInsightsStore((state) => state.actions);

export const getReferralAnalyticsFilters = ({ filters, selectedReceivingGroupType, sendingGroupTypeId }: State) => {
  if (!selectedReceivingGroupType || !sendingGroupTypeId) return null;

  const activeFilters = filtersForSelectedGroupType(filters, selectedReceivingGroupType);
  const receivingClientKey = receivingClientFilterKey(selectedReceivingGroupType);
  const receivingGroupKey = receivingGroupFilterKey(selectedReceivingGroupType);

  const {
    [START_DATE]: start,
    [END_DATE]: end,
    [receivingClientKey]: receivingClient,
    [receivingGroupKey]: receivingGroup,
    ...rest
  } = activeFilters;

  return {
    ...rest,
    [DATES]: [{ start, end }],
    receivingClient,
    receivingGroup,
    receivingGroupType: selectedReceivingGroupType.id,
    sendingGroupType: sendingGroupTypeId,
  };
};
